import React from 'react';
import { 
  Reply, 
  Calendar, 
  CalendarPlus, 
  UserCheck, 
  UserPlus, 
  Plane, 
  DollarSign, 
  FolderPlus,
  Trash2 
} from 'lucide-react';

/**
 * EmailDropdown - Reusable dropdown menu for email actions
 * Used in both email list items and email thread messages
 */
const EmailDropdown = ({
  isOpen,
  onClose,
  email,
  onReply,
  onAddToMeeting,
  onNewMeeting,
  onAddContact,
  onNewContact,
  onAddToFlight,
  onAddToFinances,
  onDelete,
  onAddAttachmentToDocuments,
  attachments = [],
  showMeetingAttachmentCount = false,
  position = 'right-4 top-16', // Default position for list items
  className = ''
}) => {
  if (!isOpen) return null;

  const handleClick = (handler) => (e) => {
    e.stopPropagation();
    if (handler) handler(email);
    onClose?.();
  };

  const nonInlineAttachments = attachments.filter(a => !a.isInline);

  return (
    <div 
      className={`absolute ${position} w-52 bg-gray-900 border border-gray-400/20 rounded-lg shadow-xl z-20 ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Reply */}
      {onReply && (
        <button
          onClick={handleClick(onReply)}
          data-testid="email-dropdown-reply"
          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10 rounded-t-lg"
        >
          <Reply size={16} className="text-purple-400" />
          Reply
        </button>
      )}

      {/* Add to Meeting */}
      {onAddToMeeting && (
        <button
          onClick={handleClick(onAddToMeeting)}
          data-testid="email-dropdown-add-meeting"
          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
        >
          <Calendar size={16} className="text-purple-400" />
          Add to Meeting
          {showMeetingAttachmentCount && nonInlineAttachments.length > 0 && (
            <span className="ml-auto text-xs text-gray-500">
              +{nonInlineAttachments.length} file{nonInlineAttachments.length > 1 ? 's' : ''}
            </span>
          )}
        </button>
      )}

      {/* New Meeting */}
      {onNewMeeting && (
        <button
          onClick={handleClick(onNewMeeting)}
          data-testid="email-dropdown-new-meeting"
          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
        >
          <CalendarPlus size={16} className="text-purple-400" />
          New Meeting
          {showMeetingAttachmentCount && nonInlineAttachments.length > 0 && (
            <span className="ml-auto text-xs text-gray-500">
              +{nonInlineAttachments.length} file{nonInlineAttachments.length > 1 ? 's' : ''}
            </span>
          )}
        </button>
      )}

      {/* Add Contact */}
      {onAddContact && (
        <button
          onClick={handleClick(onAddContact)}
          data-testid="email-dropdown-add-contact"
          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
        >
          <UserCheck size={16} className="text-purple-400" />
          Add Contact
        </button>
      )}

      {/* New Contact */}
      {onNewContact && (
        <button
          onClick={handleClick(onNewContact)}
          data-testid="email-dropdown-new-contact"
          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
        >
          <UserPlus size={16} className="text-purple-400" />
          New Contact
        </button>
      )}

      {/* Add to Flights */}
      {onAddToFlight && (
        <button
          onClick={handleClick(onAddToFlight)}
          data-testid="email-dropdown-add-flight"
          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
        >
          <Plane size={16} className="text-cyan-400" />
          Add to Flights
        </button>
      )}

      {/* Add to Finances */}
      {onAddToFinances && (
        <button
          onClick={handleClick(onAddToFinances)}
          data-testid="email-dropdown-add-finances"
          className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2 border-b border-gray-400/10"
        >
          <DollarSign size={16} className="text-green-400" />
          Add to Finances
        </button>
      )}

      {/* Add Attachments to Documents */}
      {onAddAttachmentToDocuments && nonInlineAttachments.length > 0 && (
        <>
          <div className="border-t border-gray-600 my-1"></div>
          {nonInlineAttachments.map((att, attIdx) => (
            <button
              key={att.id || attIdx}
              onClick={(e) => {
                e.stopPropagation();
                onAddAttachmentToDocuments(email.id, att);
                onClose?.();
              }}
              data-testid={`email-dropdown-add-doc-${attIdx}`}
              className="w-full text-left px-4 py-2.5 hover:bg-gray-800 text-sm text-white flex items-center gap-2"
            >
              <FolderPlus size={16} className="text-blue-400" />
              <span className="truncate flex-1">Add &quot;{att.name || att.filename}&quot; to Docs</span>
            </button>
          ))}
        </>
      )}

      {/* Delete */}
      {onDelete && (
        <button
          onClick={handleClick(onDelete)}
          data-testid="email-dropdown-delete"
          className="w-full text-left px-4 py-2.5 hover:bg-red-900 text-sm text-red-400 flex items-center gap-2 rounded-b-lg"
        >
          <Trash2 size={16} className="text-red-400" />
          Delete Email
        </button>
      )}
    </div>
  );
};

export default EmailDropdown;
